import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Mail, Briefcase } from 'lucide-react';
import { INDUSTRIES, OFFICES, NEWS, CAREERS } from '../content/site';
import { PageHero, SectionHeading } from '../components/shared/PageHero';
import { Reveal } from '../components/shared/Reveal';
import { CtaBand } from '../components/home/Sections';
import { Button, Card } from '../components/sv';

export function IndustriesPage() {
  return (
    <div data-testid="industries-page">
      <PageHero kicker="Solutions" title="Industries we serve" text="Dedicated equipment, lanes and specialists for the way your sector ships." image="/img/warehouse-loading.jpg" crumbs={[{ label: "Industries" }]} />
      <section className="py-20 md:py-24">
        <div className="sv-wrap grid md:grid-cols-2 gap-6">
          {INDUSTRIES.map(function (ind) {
            return (
              <Reveal key={ind.slug}>
                <div id={ind.slug} className="sv-card p-8 h-full flex gap-6 scroll-mt-28" data-testid={"industry-" + ind.slug}>
                  <ind.icon className="h-6 w-6 text-ink shrink-0 mt-1" strokeWidth={1.75} />
                  <div>
                    <h2 className="text-[24px] md:text-[28px] sv-display">{ind.title}</h2>
                    <p className="text-gray-700 mt-3">{ind.text}</p>
                    <Link to={ind.slug === 'commodities' ? '/services/bulk-shipping' : '/quote'} className="sv-ink-link mt-5 text-[15px]">{ind.slug === 'commodities' ? 'Bulk shipping' : 'Get a quote'} <ArrowRight className="h-4 w-4" strokeWidth={1.75} /></Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
      <CtaBand />
    </div>
  );
}

export function OfficesPage() {
  return (
    <div data-testid="offices-page">
      <PageHero kicker="Company" title="Global offices and agent network" text="Regional hubs and local agencies in 121 countries. Your account is served by the agent for your region." crumbs={[{ label: 'Global offices' }]} />
      <section className="py-20 md:py-24">
        <div className="sv-wrap">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OFFICES.map(function (o) {
              return (
                <Card key={o.city} padding="p-6" data-testid={"office-" + o.city.toLowerCase().replace(/\s/g, '-')}>
                  <p className="sv-kicker">{o.role}</p>
                  <h2 className="text-[28px] sv-display mt-2">{o.city}</h2>
                  <p className="text-gray-600 text-sm">{o.country}</p>
                  <p className="flex gap-2 text-sm text-gray-700 mt-4"><MapPin className="h-4 w-4 text-gray-600 shrink-0 mt-0.5" strokeWidth={1.75} />{o.address}</p>
                  <a href={'mailto:' + o.email} className="flex gap-2 text-sm sv-mono mt-2 sv-link" data-testid={"office-email-" + o.city.toLowerCase().replace(/\s/g, '-')}><Mail className="h-4 w-4 text-gray-600 shrink-0 mt-0.5" strokeWidth={1.75} />{o.email}</a>
                </Card>
              );
            })}
          </div>
          <div className="mt-12 sv-card p-8 md:p-10 grid md:grid-cols-12 gap-8 items-center bg-gray-50">
            <div className="md:col-span-8"><h3 className="text-[28px] sv-display">Need a local agent?</h3><p className="text-gray-700 mt-2">Open an account and we assign the agent for your country automatically. They approve your account and handle your bookings end to end.</p></div>
            <div className="md:col-span-4 flex md:justify-end"><Button to="/register" data-testid="offices-register-btn">Open an account</Button></div>
          </div>
        </div>
      </section>
    </div>
  );
}

export function NewsPage() {
  return (
    <div data-testid="news-page">
      <PageHero kicker="Newsroom" title="News and customer advisories" text="Network changes, fleet investments and digital updates from Südvik." compact crumbs={[{ label: 'Newsroom' }]} />
      <section className="py-20 md:py-24">
        <div className="sv-wrap grid gap-10">
          {NEWS.map(function (n) {
            return (
              <Reveal key={n.id}>
                <article id={n.id} className="grid md:grid-cols-12 gap-8 border-b border-gray-200 pb-10 scroll-mt-28" data-testid={"news-" + n.id}>
                  <div className="md:col-span-4 h-[200px] md:h-[220px] rounded overflow-hidden bg-gray-200"><img src={n.image} alt="" className="w-full h-full object-cover" /></div>
                  <div className="md:col-span-8">
                    <div className="text-sm text-gray-600">{n.tag} · {new Date(n.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                    <h2 className="text-[28px] md:text-[32px] sv-display mt-2">{n.title}</h2>
                    <p className="text-gray-700 mt-4">{n.excerpt} The change is available for booking now through the online platform and regional agents. Customers can view schedules, request prices and reserve space immediately.</p>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export function CareersPage() {
  return (
    <div data-testid="careers-page">
      <PageHero kicker="Careers" title="Move the world with us" text="40,000 colleagues at sea and ashore keep global trade flowing. Join a team where every role has a visible result." image="/img/quay-walk.jpg" crumbs={[{ label: "Careers" }]} />
      <section className="py-20 md:py-24">
        <div className="sv-wrap grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionHeading kicker="Why Südvik" title="A career with horizon" text="Global mobility, structured development and a fleet that is decarbonising fast." />
            <ul className="grid gap-3 text-[15px] text-gray-700">{['Rotation and shore-side roles', 'Learning academy with 300+ courses', 'Relocation support across 121 countries', 'Employee cargo and travel benefits'].map(function (b) { return <li key={b} className="flex gap-3"><span className="w-1.5 h-1.5 mt-2.5 rounded-full bg-ink shrink-0" />{b}</li>; })}</ul>
          </div>
          <div className="lg:col-span-8">
            <div className="sv-card divide-y divide-gray-200">
              {CAREERS.map(function (c) {
                return (
                  <div key={c.id} className="p-5 grid sm:grid-cols-12 gap-3 items-center hover:bg-gray-50 transition-colors duration-[120ms]" data-testid={"career-" + c.id}>
                    <div className="sm:col-span-6"><div className="text-lg">{c.title}</div><div className="text-sm text-gray-600">{c.dept}</div></div>
                    <p className="sm:col-span-3 text-sm text-gray-700 flex items-center gap-2"><MapPin className="h-4 w-4 text-gray-600" strokeWidth={1.75} />{c.location}</p>
                    <p className="sm:col-span-2 text-sm text-gray-700 flex items-center gap-2"><Briefcase className="h-4 w-4 text-gray-600" strokeWidth={1.75} />{c.type}</p>
                    <Link to="/contact" className="sm:col-span-1 text-ink justify-self-end" aria-label="Apply"><ArrowRight className="h-5 w-5" strokeWidth={1.75} /></Link>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
